import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, EyeIcon, EditIcon, CheckCircleIcon, DollarSignIcon, AlertCircleIcon } from 'lucide-react';
import { Bill } from '../types/Bill';
export function Bills() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const bills: Bill[] = [{
    id: '1',
    billNumber: 'BILL-2024-001',
    supplierId: '1',
    supplierName: 'Premium Tea Estates Ltd',
    supplierEmail: 'rajesh@premiumtea.lk',
    supplierPhone: '+94 77 123 4567',
    amount: 125000,
    currency: 'LKR',
    status: 'pending_approval',
    dueDate: '2024-02-15',
    issueDate: '2024-01-15',
    description: 'Ceylon Black Tea - January Order',
    items: [],
    subtotal: 125000,
    total: 125000,
    approvalStatus: 'pending',
    financingStatus: 'not_requested'
  }, {
    id: '2',
    billNumber: 'BILL-2024-002',
    supplierId: '2',
    supplierName: 'Spice Traders Co',
    supplierEmail: 'amara@spicetraders.lk',
    amount: 87500,
    currency: 'LKR',
    status: 'approved',
    dueDate: '2024-02-20',
    issueDate: '2024-01-20',
    description: 'Spices - January Order',
    items: [],
    subtotal: 87500,
    total: 87500,
    approvalStatus: 'approved',
    approver: 'Simon Verraest',
    approvalDate: '2024-01-22',
    financingStatus: 'not_requested'
  }, {
    id: '3',
    billNumber: 'BILL-2024-003',
    supplierId: '3',
    supplierName: 'Packaging Solutions',
    supplierEmail: 'john@packagingsolutions.lk',
    amount: 45000,
    currency: 'LKR',
    status: 'paid',
    dueDate: '2024-02-10',
    issueDate: '2024-01-10',
    paidDate: '2024-02-08',
    description: 'Packaging Materials - January',
    items: [],
    subtotal: 45000,
    total: 45000,
    approvalStatus: 'approved',
    approver: 'Simon Verraest',
    approvalDate: '2024-01-12',
    financingStatus: 'not_requested'
  }];
  const getStatusBadge = (status: Bill['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const labels = {
      draft: 'Draft',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      paid: 'Paid',
      overdue: 'Overdue',
      rejected: 'Rejected'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-2915">
        {labels[status]}
      </span>;
  };
  const filteredBills = bills.filter(bill => {
    const matchesSearch = bill.billNumber.toLowerCase().includes(searchTerm.toLowerCase()) || bill.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) || bill.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || bill.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const totalBills = filteredBills.length;
  const pendingApproval = filteredBills.filter(b => b.status === 'pending_approval').length;
  const totalAmount = filteredBills.reduce((sum, b) => sum + b.amount, 0);
  const unpaidAmount = filteredBills.filter(b => b.status !== 'paid').reduce((sum, b) => sum + b.amount, 0);
  return <div className="space-y-6" data-id="element-2916">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2917">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-2918">
          <div className="text-sm font-medium text-gray-500" data-id="element-2919">Total Bills</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-2920">
            {totalBills}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-2921">
          <div className="text-sm font-medium text-gray-500" data-id="element-2922">
            Pending Approval
          </div>
          <div className="mt-2 text-3xl font-semibold text-yellow-600" data-id="element-2923">
            {pendingApproval}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-2924">
          <div className="text-sm font-medium text-gray-500" data-id="element-2925">Total Amount</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-2926">
            Rs. {totalAmount.toLocaleString('en-LK')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-2927">
          <div className="text-sm font-medium text-gray-500" data-id="element-2928">Unpaid Amount</div>
          <div className="mt-2 text-3xl font-semibold text-red-600" data-id="element-2929">
            Rs. {unpaidAmount.toLocaleString('en-LK')}
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-2930">
        {/* Header */}
        <div className="p-6 border-b border-gray-200" data-id="element-2931">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2932">
            <div className="flex-1 max-w-lg" data-id="element-2933">
              <div className="relative" data-id="element-2934">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-2935" />
                <input type="text" placeholder="Search bills..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-2936" />
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-2937">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-2938">
                <option value="all" data-id="element-2939">All Status</option>
                <option value="draft" data-id="element-2940">Draft</option>
                <option value="pending_approval" data-id="element-2941">Pending Approval</option>
                <option value="approved" data-id="element-2942">Approved</option>
                <option value="paid" data-id="element-2943">Paid</option>
                <option value="overdue" data-id="element-2944">Overdue</option>
                <option value="rejected" data-id="element-2945">Rejected</option>
              </select>
              <button onClick={() => navigate('/bill-form')} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-2946">
                <PlusIcon size={20} data-id="element-2947" />
                New Bill
              </button>
            </div>
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto" data-id="element-2948">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2949">
            <thead className="bg-gray-50" data-id="element-2950">
              <tr data-id="element-2951">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2952">
                  Bill Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2953">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2954">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2955">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2956">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2957">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2958">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2959">
              {filteredBills.map(bill => <tr key={bill.id} className="hover:bg-gray-50" data-id="element-2960">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2961">
                    <div className="text-sm font-medium text-gray-900" data-id="element-2962">
                      {bill.billNumber}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2963">
                      {new Date(bill.issueDate).toLocaleDateString('en-GB')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2964">
                    <div className="text-sm font-medium text-gray-900" data-id="element-2965">
                      {bill.supplierName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2966">
                      {bill.supplierEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-2967">
                    <div className="text-sm text-gray-900" data-id="element-2968">
                      {bill.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2969">
                    <div className="text-sm font-medium text-gray-900" data-id="element-2970">
                      Rs. {bill.amount.toLocaleString('en-LK')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2971">
                    <div className="text-sm text-gray-900" data-id="element-2972">
                      {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2973">
                    {getStatusBadge(bill.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2974">
                    <div className="flex items-center justify-end gap-2" data-id="element-2975">
                      <button onClick={() => navigate(`/bill-detail/${bill.id}`)} className="text-blue-600 hover:text-blue-900" title="View" data-id="element-2976">
                        <EyeIcon size={18} data-id="element-2977" />
                      </button>
                      {bill.status === 'draft' && <button onClick={() => navigate(`/bill-form?id=${bill.id}`)} className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-2978">
                          <EditIcon size={18} data-id="element-2979" />
                        </button>}
                      {bill.status === 'pending_approval' && <button onClick={() => navigate(`/bill-approval/${bill.id}`)} className="text-green-600 hover:text-green-900" title="Approve" data-id="element-2980">
                          <CheckCircleIcon size={18} data-id="element-2981" />
                        </button>}
                      {bill.status === 'approved' && bill.financingStatus === 'not_requested' && <button onClick={() => navigate(`/bill-financing/${bill.id}`)} className="text-purple-600 hover:text-purple-900" title="Request Financing" data-id="element-2982">
                            <DollarSignIcon size={18} data-id="element-2983" />
                          </button>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {filteredBills.length === 0 && <div className="text-center py-12" data-id="element-2984">
            <p className="text-gray-500" data-id="element-2985">No bills found</p>
          </div>}
      </div>
    </div>;
}